<template>
    <div
        class="request-comment request-comment_light"
        :class="{
            'request-comment_dark': isFirstComment,
            'request-comment_border-light': isUserAuthorOfComment,
        }"
    >
        <div class="request-comment__header">
            <UserCardWithPopover
                multiline
                :account="comment.author"
                :status="$_toLocalDate(comment.createdAt)"
            ></UserCardWithPopover>
        </div>

        <ChatReplyQuote
            class="request-comment__reply"
            v-if="isReply"
            interactive
            :dark="isFirstComment"
            :preview="replyQuote"
            @click="$emit('quoteClick', comment.replyToId)"
        ></ChatReplyQuote>

        <p class="request-comment__content" v-if="chunks && chunks.length > 0">
            <template v-for="(chunk, index) in chunks">
                <span
                    class="request-comment__content-chunk"
                    :key="index"
                    v-text="chunk"
                    v-linkified:options="{
                        validate: validate,
                        defaultProtocol: 'https',
                    }"
                    v-if="chunk"
                ></span>

                <br :key="index + '-br'" />
            </template>
        </p>
        <p class="request-comment__content-edited">
            <span
                v-tooltip="
                    (isReadByAuthor ? 'Прочитано автором\n' : '') + (isReadByExecutor ? 'Прочитано исполнителем' : '')
                "
            >
                <span v-if="comment.createdAt != comment.updatedAt">(изменено)</span>

                <span
                    class="request-comment__read-status"
                    :style="{
                        '--tick-double-first-color':
                            isReadByAuthor || isReadByExecutor
                                ? 'var(--color-green)'
                                : isFirstComment
                                ? '#aeaeae'
                                : '#aaaaaa',
                        '--tick-double-second-color':
                            isReadByAuthor && isReadByExecutor
                                ? 'var(--color-green)'
                                : isFirstComment
                                ? '#aeaeae'
                                : '#aaaaaa',
                    }"
                >
                    <TickDoubleSvg class="request-comment__story-icon"></TickDoubleSvg>
                </span>
            </span>
        </p>

        <ul class="request-comment__attachments" v-if="comment.files && comment.files.length > 0">
            <template v-for="(attachment, index) in attachments">
                <li class="request-comment__attachment" :key="index">
                    <ChatCommentAttachment :dark="isFirstComment" :attachment="attachment"></ChatCommentAttachment>
                </li>
            </template>
        </ul>

        <div></div>
    </div>
</template>
<script>
// Svg
import TickDoubleSvg from '@/images/tick-double.svg';

// Components
import UserCardWithPopover from '@/views/components/VUser/UserCardWithPopover.vue';

import ChatCommentAttachment from './ChatCommentAttachment.vue';
import ChatReplyQuote from './ChatReplyQuote.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import RequestCommentType from '../../../../js/enums/RequestCommentType';
import { hasReply, replyPreview } from '@/js/chatReply';

@Component({
    components: {
        TickDoubleSvg,
        UserCardWithPopover,
        ChatCommentAttachment,
        ChatReplyQuote,
    },
})
class ChatComment extends Vue {
    @Prop({ type: Object, required: true })
    request;

    @Prop({ type: Object, required: true })
    comment;

    // Исходное сообщение, на которое отвечает это; null — удалено или недоступно.
    @Prop({ type: Object, default: null })
    replySource;

    get isReply() {
        return hasReply(this.comment);
    }

    get replyQuote() {
        return replyPreview(this.replySource);
    }

    validate(uri) {
        if (typeof uri == 'string') {
            return uri.match(/^https:\/\/[a-zA-Z0-9][a-zA-Z0-9-]+\.talan\.group/);
        }

        return false;
    }

    get chunks() {
        if (!this.comment?.text) {
            return [];
        }

        return this.comment?.text
            .replace(/<span class='chat-text-color-blue'>|<\/span>/g, '') //bug fix
            .replace('<br />', '\n')
            .split('\n');
    }

    get attachments() {
        return this.comment.files;
    }

    get isFirstComment() {
        // If a comment is the first comment, we should highlight it.
        return this.comment.type == RequestCommentType.FIRST_COMMENT;
    }

    get isUserAuthorOfComment() {
        return this.comment.authorId == this.$_account.id;
    }

    get isReadByAuthor() {
        return this.isCommentReadByUser(this.comment, this.request.authorId);
    }

    get isReadByExecutor() {
        return this.isCommentReadByUser(this.comment, this.request.executorId);
    }

    isCommentReadByUser(comment, userId) {
        if (comment.authorId === userId) {
            return true;
        }

        const lastSeenCommentByUser = this.request.lastSeenComments.find((comment) => comment.userId === userId);
        return lastSeenCommentByUser?.commentId >= comment.id;
    }
}

export default ChatComment;
</script>
<style lang="scss">
:root {
    --request-comment-dark-color: #fff;
    --request-comment-dark-background: #484848;

    --request-comment-light-color: #484848;
    --request-comment-light-background: #f8f8f8;

    --request-comment-border-light-border: #e4eaef;
    --request-comment-border-light-background: #fff;
}

.request-comment {
    display: inline-block;
    min-width: 320px;
    max-width: 75%;
    margin: 0 24px;
    padding: 16px;
    border-radius: 10px;

    &_light {
        color: var(--request-comment-light-color);
        background: var(--request-comment-light-background);
    }

    &_border-light {
        border: 1px solid var(--request-comment-border-light-border);
        background: var(--request-comment-border-light-background);
    }

    &_dark {
        color: var(--request-comment-dark-color);
        background: var(--request-comment-dark-background);

        & .user-card__label-name {
            color: var(--request-comment-dark-color);
        }
    }

    &__content,
    &__attachment {
        margin: 8px 0 0 0;
    }

    // На узком экране пузырь уже 330px карточки вложения: потолок переносится на
    // пункт списка, и карточка сжимается по пузырю — имя обрезается, кнопки остаются.
    &__attachment {
        max-width: 330px;
    }

    &__attachment .comment-attachment {
        max-width: 100%;
    }

    &__header .user-card__label {
        gap: 3px;
    }

    &__header .user-card__label-name,
    &__header .user-card__label-status {
        line-height: 1.25;
    }

    &__header .user-card__label-status {
        white-space: normal;
    }

    &__content-chunk {
        word-wrap: break-word;
        line-height: 1.4;
    }

    &__reply {
        margin: 8px 0 0 0;
    }

    &__content-edited {
        font-size: 0.75rem;
        line-height: 0.875rem;
        color: var(--user-card-label-status-color);
        text-align: right;
        margin: 0;
    }

    &__read-status {
        margin-left: 0.25rem;

        & svg {
            width: 1.25rem;
        }
    }
}
</style>
